<script lang="ts">
  import Cookies from 'js-cookie';
  import { getContext, onMount } from 'svelte';
  import { fade, slide } from 'svelte/transition';
  import { Button, Link } from '@squareup/public-web-design-system';
  import ModalPartial from '$components/common/partials/ModalPartials/ModalPartial.svelte';
  import AvailableLocalesPartial from '$components/common/partials/AvailableLocalesPartial/AvailableLocalesPartial.svelte';
  import CaretPartial from '$components/common/partials/CaretPartial/CaretPartial.svelte';
  import countryDetectionHelper from '$lib/helpers/country-detection-helper';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';
  import { SQUARE_GEO_COOKIE_NAME } from '$lib/constants/cookie-constants';

  const designSystem = getContext<DesignSystemEnum>('designSystem');

  let isExpanded: boolean = false;

  let availableLocalesHeading: string | undefined = '',
    proceedText: string | undefined = '',
    switchCountryButtonText: string | undefined = '',
    switchCountryButtonUrl: string | undefined = '',
    title: string | undefined = '';

  export let modalPartialConfig: ModalPartialConfig;
  export let locale: string;
  export let defaultLocales: DefaultLocales;
  export let supportedLocales: string[];
  export let supportedCountriesAndLangs: CountryAndLanguageNamesMap;

  const onClick = () => {
    isExpanded = !isExpanded;
  };

  const closeModal = () => {
    modalPartialConfig.active = false;
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    Cookies.set('country_detection', 'disabled');
  };

  onMount(async () => {
    const userGeolocatedCountry = countryDetectionHelper.extractCountryFromGeoCode(
      Cookies.get(SQUARE_GEO_COOKIE_NAME) ?? null
    );
    const currentPath = window.location.pathname;

    // If user geolocated country is null, don't show the modal
    if (
      !Cookies.get('country_detection') &&
      userGeolocatedCountry &&
      countryDetectionHelper.detectCountryMismatch(locale, userGeolocatedCountry, supportedCountriesAndLangs)
    ) {
      const browserPreferredLocale = window.navigator.language;

      ({ title, availableLocalesHeading, switchCountryButtonText, switchCountryButtonUrl, proceedText } =
        await countryDetectionHelper.getLocalizedCountryDetectionModalStringsAndUrls(
          locale,
          userGeolocatedCountry,
          browserPreferredLocale,
          supportedLocales,
          defaultLocales,
          currentPath
        ));

      modalPartialConfig.title = title;
      modalPartialConfig.proceedText = proceedText;
      modalPartialConfig.active = true;
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }
  });
</script>

<ModalPartial {modalPartialConfig}>
  <div class="changeCountryContainer">
    {#if designSystem === DesignSystemEnum.PublicWebDesignSystem}
      <div>
        <Link variant="button" href={switchCountryButtonUrl} text={switchCountryButtonText} />
      </div>
      <Button variant="text" text={modalPartialConfig.proceedText} onClick={closeModal} />
    {:else}
      <div>
        <a class="changeCountryButton" href={switchCountryButtonUrl}>{switchCountryButtonText}</a>
      </div>
      <button on:click={closeModal} class="proceedButton arrow">{modalPartialConfig.proceedText}</button>
    {/if}
  </div>
  <div class="borderVert">
    <button class="availableLanguagesHeadingBlock" on:click={onClick}>
      <div class="languageAndCaretContainer">
        <div class="availableLanguagesHeading">
          {availableLocalesHeading}
        </div>
        <CaretPartial {isExpanded} />
      </div>
    </button>
  </div>
  {#if isExpanded}
    <div transition:fade>
      <div transition:slide>
        <AvailableLocalesPartial {supportedCountriesAndLangs} />
      </div>
    </div>
  {/if}
</ModalPartial>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/ui.scss' as ui;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/grid.scss' as grid;

  .arrow {
    position: relative;
  }

  .arrow::after {
    content: '→';
    pointer-events: none;
    display: inline-block;
    margin-left: 0.25em;
    transition: transform var(--duration-quick) var(--easing-sharp);
  }

  .availableLanguagesHeadingBlock {
    display: block;
    padding-bottom: var(--space-30);
    padding-top: var(--space-30);
    color: var(--color-text);
    font-weight: var(--font-weight-medium);
    width: 100%;
  }

  .availableLanguagesHeading {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    text-align: left;
  }

  a {
    font-size: var(--font-size-16);
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    font-weight: var(--font-weight-medium);
    font-family: var(--font-family-base);
    font-size: var(--font-size-button);
    line-height: var(--line-height-button);
    padding: 0;
    text-decoration: none;
  }

  .borderVert {
    border-top: 1px solid var(--color-gray-15);
    border-bottom: 1px solid var(--color-gray-15);
    margin-top: var(--space-20);
  }

  .changeCountryContainer {
    @include grid.columns(1);
    @include grid.space(20);
  }

  .changeCountryButton {
    @include ui.button;
  }

  .languageAndCaretContainer {
    @include grid.flex;
    @include grid.space(20);
  }

  .proceedButton {
    color: var(--color-blue);
    text-align: left;
  }
</style>
